import { BehaviorSubject, Observable, shareReplay } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { CftAssessment } from '../models/cool-farm-tool-assessment.model';
import { MappingData } from '../../cft/create-assessment/types';

@Injectable()
export class CftAssessmentService {
  private endpoint = environment.apiBaseUrl;
  private pathCft = 'api/private/v1/livestock/cft';
  private pathCftMapping = 'api/private/v1/cft/mapping';

  // Keeps track of:

  // All dairy assessments
  $dairyAssessments: BehaviorSubject<CftAssessment[]> = new BehaviorSubject<CftAssessment[]>([]);

  // Details of the CftAssessment which the user is looking at
  $dairyAssessment: BehaviorSubject<CftAssessment> = new BehaviorSubject<CftAssessment>(null);

  // Mapping of the translated options for the assessment
  mapping: Observable<MappingData> = new Observable<MappingData>(null);

  http = inject(HttpClient);

  constructor() {
    // Load the mapping data only one time
    this.mapping = this.http
      .get<MappingData>(`${this.endpoint}${this.pathCftMapping}`)
      .pipe(shareReplay(1));
  }

  /**
   * @description: Get all assessments
   * @returns {Promise<CftAssessment[]>}
   */
  async getCftAssessments(all: boolean = false): Promise<CftAssessment[]> {
    const response = await this.http
      .get<CftAssessment[]>(`${this.endpoint}${this.pathCft}?all=${all}`)
      .toPromise();
    this.$dairyAssessments.next(response);
    return response;
  }

  /**
   * @description: Get the whole object of a specific assessment by ID
   * @param id: {string} the id of the path
   * @returns {Promise<CftAssessment>}
   */
  async getAssessmentById(id: number): Promise<CftAssessment> {
    const response = await this.http
      .get<CftAssessment>(`${this.endpoint}${this.pathCft}/${id}?all=true`)
      .toPromise();
    this.$dairyAssessment.next(response);
    return response;
  }

  /**
   * @description: Create a new assessment
   * @returns {Promise<CftAssessment>}
   * @param year: {number} year related to the assessment
   */
  async createAssessment(year: number): Promise<CftAssessment> {
    return await this.http
      .post<CftAssessment>(`${this.endpoint}${this.pathCft}?all=true`, { year })
      .toPromise();
  }

  /**
   * @description: Update an existing assessment
   * @returns {Promise<CftAssessment>}
   * @param assessment
   */
  async updateAssessment(assessment: CftAssessment): Promise<CftAssessment> {
    return await this.http
      .put<CftAssessment>(`${this.endpoint}${this.pathCft}/${assessment.id}?all=true`, assessment)
      .toPromise();
  }

  /**
   * @description: Delete a assessment by ID
   * @returns {Promise<void>} - Promise resolving to void, otherwise throws error
   * @param id
   */
  async deleteAssessment(id: number): Promise<void> {
    await this.http.delete(`${this.endpoint}${this.pathCft}/${id}?all=true`).toPromise();
  }

  /**
   * @description: Delete a assessment by ID
   * @returns {Promise<void>} - Promise resolving to void, otherwise throws error
   * @param id
   */
  async sendAssessment(id: number): Promise<void> {
    await this.http.post(`${this.endpoint}${this.pathCft}/${id}?all=true`, {}).toPromise();
  }
}
